import React, { forwardRef } from 'react';
import { Box } from '@mui/joy';
import { SectionOverlay } from './SectionOverlay';
import { DropZone } from './DropZone';
import type {
  EditorOverlayContainerProps,
} from '../../types/visualEditor';

export const EditorOverlayContainer = forwardRef<HTMLDivElement, EditorOverlayContainerProps>(
  (
    {
      iframeRef,
      sections,
      manifest,
      sectionBounds,
      selectedIndex,
      hoveredIndex,
      isDragging,
      dragSourceIndex,
      dragOverIndex,
      iframeScrollTop,
      iframeScale,
      onSectionSelect,
      onSectionHover,
      onSectionReorder,
      onAddSection,
      onDeleteSection,
      onDuplicateSection,
      onEditSection,
      onStartDrag,
      onDragOver,
      onEndDrag,
    },
    ref
  ) => {
    return (
      <Box
        ref={ref}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none', // Allow clicks to pass through to iframe
          overflow: 'hidden',
        }}
      >
        {/* Drop zone at the very top (before first section) */}
        <DropZone
          afterIndex={-1}
          isActive={isDragging}
          isDragOver={dragOverIndex === -1}
          bounds={
            sectionBounds.get(0)
              ? {
                  top: 0,
                  left: 0,
                  width: sectionBounds.get(0)?.bounds.width || 0,
                  height: 0,
                  bottom: 0,
                  right: sectionBounds.get(0)?.bounds.width || 0,
                }
              : undefined
          }
          iframeScrollTop={iframeScrollTop}
          onDrop={(sourceIndex) => {
            if (sourceIndex !== 0) {
              onSectionReorder(sourceIndex, 0);
            }
            onEndDrag();
          }}
          onAddClick={() => onAddSection(-1)}
          onDragOver={() => onDragOver(-1)}
        />

        {/* Section overlays and drop zones */}
        {sections.map((section, index) => {
          const bounds = sectionBounds.get(index);
          const sectionConfig = manifest[section.type];

          if (!bounds) {
            // Section bounds not yet available
            return null;
          }

          return (
            <React.Fragment key={`section-${index}`}>
              {/* Section overlay */}
              <SectionOverlay
                index={index}
                section={section}
                sectionConfig={sectionConfig}
                bounds={bounds.bounds}
                iframeScrollTop={iframeScrollTop}
                isSelected={selectedIndex === index}
                isHovered={hoveredIndex === index && selectedIndex !== index}
                isDragging={isDragging && dragSourceIndex === index}
                isDragOver={dragOverIndex === index}
                onSelect={() => onSectionSelect(index)}
                onStartDrag={() => onStartDrag(index)}
                onDelete={() => onDeleteSection(index)}
                onDuplicate={() => onDuplicateSection(index)}
                onMoveUp={() => {
                  if (index > 0) {
                    onSectionReorder(index, index - 1);
                  }
                }}
                onMoveDown={() => {
                  if (index < sections.length - 1) {
                    onSectionReorder(index, index + 1);
                  }
                }}
                onEdit={() => onEditSection(index)}
                canMoveUp={index > 0}
                canMoveDown={index < sections.length - 1}
              />

              {/* Drop zone after this section */}
              <DropZone
                afterIndex={index}
                isActive={isDragging}
                isDragOver={dragOverIndex === index + 1}
                bounds={{
                  top: bounds.bounds.bottom,
                  left: bounds.bounds.left,
                  width: bounds.bounds.width,
                  height: 0,
                  bottom: bounds.bounds.bottom,
                  right: bounds.bounds.right,
                }}
                iframeScrollTop={iframeScrollTop}
                onDrop={(sourceIndex) => {
                  // Calculate target index accounting for movement
                  let targetIndex = index + 1;
                  if (sourceIndex < targetIndex) {
                    targetIndex -= 1; // Adjust for the source being removed
                  }
                  if (sourceIndex !== targetIndex) {
                    onSectionReorder(sourceIndex, targetIndex);
                  }
                  onEndDrag();
                }}
                onAddClick={() => onAddSection(index)}
                onDragOver={() => onDragOver(index + 1)}
              />
            </React.Fragment>
          );
        })}
      </Box>
    );
  }
);

EditorOverlayContainer.displayName = 'EditorOverlayContainer';
